import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import './ncr-tasks-table.css';

import { DateRange, DateRangeDropdown, DateRangeDropdownHandle } from '../Filters/DateRangeDropdown';
import { EnumSelectDropdown, EnumSelectDropdownHandle } from '../Filters/EnumSelectDropdown';
import WordSearchInput, { WordSearch, WordSearchInputHandle } from '../Filters/WordSearchInput';
import ExclamationTriangle from '../Icons/ExclamationTriangle';
import ResetFilter from '../Icons/ResetFilter';

import { Sort } from '../../enums/Sort';

import {
    Stage,
    StageLabel,
    NCRTier,
    NCRTierLabel,
    NCRType,
    NCRTypeLabel,
} from '../../enums/NCR';

import { isIsoDateInRange, stripToYmdStrict } from '../../functions/TimeHelpers';

import EmployeeDetail from '../../types/Employee/EmployeeDetail';
import ITask from '../../types/Task/Task';

type NCRTasksTableProps = {
    taskList: ITask[];
    currentUserEmail: string;
    currentUserJobCode?: string;
    onGroupClick: (task: ITask) => void;
};

const NCRTasksTable = ({
    taskList,
    currentUserEmail,
    currentUserJobCode,
    onGroupClick,
}: NCRTasksTableProps) => {
    const [filterDueDate, setFilterDueDate] = useState<DateRange>({ sort: Sort.Ascending });
    const [filterReportID, setFilterReportID] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [filterDescription, setFilterDescription] = useState('');
    const [filterStage, setFilterStage] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterTier, setFilterTier] = useState('');
    const [filterAssignee, setFilterAssignee] = useState('');

    const [filteredList, setFilteredList] = useState<ITask[]>(taskList);

    const dateRangeRef = useRef<DateRangeDropdownHandle>(null);
    const stageEnumSelectRef = useRef<EnumSelectDropdownHandle>(null);
    const typeEnumSelectRef = useRef<EnumSelectDropdownHandle>(null);
    const tierEnumSelectRef = useRef<EnumSelectDropdownHandle>(null);
    const reportIDRef = useRef<WordSearchInputHandle>(null);
    const projectRef = useRef<WordSearchInputHandle>(null);
    const descriptionRef = useRef<WordSearchInputHandle>(null);
    const assigneeRef = useRef<WordSearchInputHandle>(null);

    const searchInGroup = (searchParam: string, group: EmployeeDetail[]): boolean => {
        if (searchParam === '') return false;
        return group.some(x => x.employeeName.toLowerCase().includes(searchParam.toLowerCase()));
    };

    const processDateCompare = (date: string): boolean => {
        const dueDate = new Date(date);
        return dueDate.getTime() < Date.now();
    };

    // Filter and sort tasks when dependencies change
    useEffect(() => {
        const hasDateSort = filterDueDate?.sort !== undefined;

        const filtered = taskList
            .filter((x) => (
                isIsoDateInRange(stripToYmdStrict(x.dueDate), filterDueDate.start, filterDueDate.end)
                && x.ncrID.toLowerCase().includes(filterReportID.toLowerCase())
                && `${x.projectNumber} - ${x.projectName}`.toLowerCase().includes(filterProject.toLowerCase())
                && x.description.toLowerCase().includes(filterDescription.toLowerCase())
                && (x.stage.toString() === filterStage || filterStage === '')
                && (x.ncrType === filterType || filterType === '')
                && (x.tier.toString() === filterTier || filterTier === '')
                && (
                    x.assignedToName.toLowerCase().includes(filterAssignee.toLowerCase())
                    || (x.assignedToName === '' && searchInGroup(filterAssignee, x.groupUsers ?? []))
                )
            ));

        if (!hasDateSort) {
            setFilteredList(filtered);
            return;
        }

        // Sort by due date
        const sorted = [...filtered].sort((a, b) => {
            const dateA = new Date(a.dueDate).getTime();
            const dateB = new Date(b.dueDate).getTime();

            const cmp = dateA - dateB;
            return filterDueDate.sort === Sort.Ascending ? cmp : -cmp;
        });

        setFilteredList(sorted);
    }, [taskList, filterDueDate, filterReportID, filterProject, filterDescription, filterStage, filterType, filterTier, filterAssignee]);

    const resetFilters = () => {
        dateRangeRef.current?.clear();
        stageEnumSelectRef.current?.clear();
        typeEnumSelectRef.current?.clear();
        tierEnumSelectRef.current?.clear();
        reportIDRef.current?.clear();
        projectRef.current?.clear();
        descriptionRef.current?.clear();
        assigneeRef.current?.clear();
        setFilterDueDate({ sort: Sort.Ascending });
        setFilterReportID('');
        setFilterProject('');
        setFilterDescription('');
        setFilterStage('');
        setFilterType('');
        setFilterTier('');
        setFilterAssignee('');
    };

    const hasActiveFilters = filterReportID || filterProject || filterDescription || filterStage || filterType || filterTier || filterAssignee || filterDueDate.start || filterDueDate.end;

    const renderNoList = () => (
        <tr>
            <td></td>
            <td className='text-muted'>No Tasks Found</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
    );

    const renderList = () => (
        filteredList.map((task) => (
            <tr key={task.id}>
                <td>
                    {processDateCompare(task.dueDate) && (
                        <ExclamationTriangle size={20} strokeColor="red" fillColor='red' markColor="white" />
                    )}
                </td>
                <td className={processDateCompare(task.dueDate) ? 'ncr-due-date-overdue' : ''}>
                    {new Date(task.dueDate).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                </td>
                <td>
                    <Link to={{ pathname: `/taskdetails/${task.id}`, state: currentUserJobCode }}>
                        {task.ncrID}
                    </Link>
                </td>
                <td>{task.projectNumber} - {task.projectName}</td>
                <td>{task.description}</td>
                <td>{StageLabel[task.stage]}</td>
                <td>{task.ncrType}</td>
                <td>{String(task.tier)}</td>
                <td>
                    {task.assignedToName ? (
                        task.assignedToName
                    ) : (
                        <button
                            onClick={() => onGroupClick(task)}
                            className="ncr-group-button"
                        >
                            Group
                        </button>
                    )}
                </td>
                <td></td>
            </tr>
        ))
    );

    return (
        <div className="border rounded-2 shadow-sm ncr-tasks">
            {/* Filter Controls Row */}
            <div className="ncr-tasks-controls border-bottom mb-1 py-2">
                <div>{/* Icon column - empty */}</div>
                <div>
                    <DateRangeDropdown
                        ref={dateRangeRef}
                        label='Due Date'
                        wrapperClassName='mb-0'
                        onChange={(r) => setFilterDueDate(r)}
                        sortable={true}
                        sort={filterDueDate.sort}
                    />
                </div>
                <div>
                    <WordSearchInput
                        ref={reportIDRef}
                        label='Report ID'
                        wrapperClassName='mb-0'
                        placeholder="Report ID..."
                        onChange={(search: WordSearch) => setFilterReportID(search.value ?? '')}
                    />
                </div>
                <div>
                    <WordSearchInput
                        ref={projectRef}
                        label='Project'
                        wrapperClassName='mb-0'
                        placeholder="Project..."
                        onChange={(search: WordSearch) => setFilterProject(search.value ?? '')}
                    />
                </div>
                <div>
                    <WordSearchInput
                        ref={descriptionRef}
                        label='Description'
                        wrapperClassName='mb-0'
                        placeholder="Description..."
                        onChange={(search: WordSearch) => setFilterDescription(search.value ?? '')}
                    />
                </div>
                <div>
                    <EnumSelectDropdown
                        ref={stageEnumSelectRef}
                        label='Stage'
                        enumObj={Stage}
                        labels={{ SMPMApproval: 'SM/PM Approval' }}
                        wrapperClassName='mb-0'
                        onChange={(stage) => setFilterStage(stage?.toString() ?? '')}
                    />
                </div>
                <div>
                    <EnumSelectDropdown
                        ref={typeEnumSelectRef}
                        label='Type'
                        enumObj={NCRType}
                        wrapperClassName='mb-0'
                        onChange={(type) => setFilterType(type !== undefined ? NCRTypeLabel[type] : '')}
                    />
                </div>
                <div>
                    <EnumSelectDropdown
                        ref={tierEnumSelectRef}
                        label='Tier'
                        enumObj={NCRTier}
                        wrapperClassName='mb-0'
                        onChange={(tier) => setFilterTier(tier !== undefined ? NCRTierLabel[tier] : '')}
                        labels={{
                            Tier1: 'Tier 1',
                            Tier2: 'Tier 2',
                            Tier3: 'Tier 3',
                        }}
                    />
                </div>
                <div>
                    <WordSearchInput
                        ref={assigneeRef}
                        label='Assignee'
                        wrapperClassName='mb-0'
                        placeholder="Assignee..."
                        onChange={(search: WordSearch) => setFilterAssignee(search.value ?? '')}
                    />
                </div>
                <div>
                    <label className="form-label">&nbsp;</label>
                    <button
                        title='Reset all filters'
                        className='ncr-reset-btn'
                        onClick={resetFilters}
                    >
                        <ResetFilter />
                    </button>
                </div>
            </div>

            {/* Scrollable Table Container */}
            <div className='ncr-tasks-table-container'>
                <table className='ncr-tasks-table'>
                    <colgroup>
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    <tbody>
                        {filteredList.length > 0 ? renderList() : renderNoList()}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default NCRTasksTable;
